import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Ship, Truck, Clock, FileClock, CheckCircle2, Inbox, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { api, STATUS_LABELS } from "@/lib/api";

const CARDS = [
  { key: "available", label: "Available", icon: Ship, color: "text-mtm-teal-dark bg-mtm-teal/10" },
  { key: "coming_soon", label: "Coming Soon", icon: Clock, color: "text-mtm-violet bg-mtm-violet/10" },
  { key: "en_route", label: "En Route", icon: Truck, color: "text-mtm-teal bg-mtm-teal/10" },
  { key: "pending", label: "Pending", icon: FileClock, color: "text-amber-600 bg-amber-100" },
  { key: "sold", label: "Sold", icon: CheckCircle2, color: "text-red-600 bg-red-100" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-slate-500">Loading...</div>;
  const w = data.seo_warnings || {};
  const warnTotal = (w.missing_title?.length || 0) + (w.missing_meta?.length || 0) + (w.missing_alt?.length || 0);

  return (
    <div data-testid="admin-dashboard">
      <AdminHeader title="Dashboard" subtitle="Overview of your dealership inventory and leads"
        action={<Button asChild className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full"><Link to="/admin/inventory/new" data-testid="dashboard-add-boat-button"><Plus size={16} /> Add Boat</Link></Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {CARDS.map((c) => (
          <div key={c.key} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`stat-${c.key}`}>
            <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${c.color}`}><c.icon size={20} /></span>
            <div className="text-3xl font-heading font-extrabold text-slate-900 mt-3">{data.counts[c.key] || 0}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wide font-mono mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Link to="/admin/leads" className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid="stat-new-leads">
          <span className="h-10 w-10 rounded-lg bg-mtm-purple/10 text-mtm-purple flex items-center justify-center"><Inbox size={20} /></span>
          <div className="text-3xl font-heading font-extrabold text-slate-900 mt-3">{data.new_leads}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide font-mono mt-0.5">New Leads ({data.total_leads} total)</div>
        </Link>
        <Link to="/admin/seo" className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid="stat-seo-warnings">
          <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${warnTotal ? "bg-amber-100 text-amber-600" : "bg-mtm-teal/10 text-mtm-teal-dark"}`}><AlertTriangle size={20} /></span>
          <div className="text-3xl font-heading font-extrabold text-slate-900 mt-3">{warnTotal}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide font-mono mt-0.5">SEO Warnings</div>
        </Link>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <span className="h-10 w-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><Ship size={20} /></span>
          <div className="text-3xl font-heading font-extrabold text-slate-900 mt-3">{data.counts.total}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wide font-mono mt-0.5">Total Boats</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6">
        <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Recent Inventory</h2>
        <div className="divide-y divide-slate-100">
          {(data.recent_inventory || []).map((b) => (
            <Link key={b.id} to={`/admin/inventory/${b.id}`} className="flex items-center justify-between py-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg">
              <div><div className="font-semibold text-slate-900 text-sm">{b.year} {b.brand} {b.model}</div><div className="text-xs text-slate-400 font-mono">#{b.stock_number || "N/A"} · {b.condition}</div></div>
              <span className="text-xs font-semibold text-mtm-purple">{STATUS_LABELS[b.status]}</span>
            </Link>
          ))}
          {!(data.recent_inventory || []).length && <p className="text-sm text-slate-400 py-4">No inventory yet.</p>}
        </div>
      </div>
    </div>
  );
}
